// Haircuts Studio Presets Data
// Each preset represents a haircut style with preview image and AI prompt style

const SUPPORTED_GENDERS = new Set(['men', 'women', 'other']);

/**
 * Builds a local preview image path based on preset id.
 * Convention:
 * - id: "men-low-taper-fade"
 * - file: "low_taper_fade_men.png"
 * - path: "/assets/haircuts/men/low_taper_fade_men.png"
 */
const getLocalPreviewImageFromId = (id) => {
  if (!id || typeof id !== 'string') return null;

  const gender = id.split('-')[0];
  if (!SUPPORTED_GENDERS.has(gender)) return null;

  const base = id.replace(`${gender}-`, '').replace(/-/g, '_');
  const ext = gender === 'women' ? 'jpeg' : 'png';
  return `/assets/haircuts/${gender}/${base}_${gender}.${ext}`;
};

const rawHaircutPresets = [
  // ===== MEN'S HAIRCUTS =====
  {
    id: 'men-textured-french-crop',
    name: 'Textured French Crop',
    genderTags: ['men', 'other'],
    category: 'short',
    localPreviewImage: getLocalPreviewImageFromId('men-textured-french-crop'),
    previewImage: 'https://images.unsplash.com/photo-1605497788044-5a32c7078486?w=400&h=400&fit=crop', // Placeholder
    promptStyle: "a man with this haircut: Short, choppy top with a blunt fringe and a tidy fade."
  },
  {
    id: 'men-low-taper-fade',
    name: 'Soft Taper / Natural Fade',
    genderTags: ['men', 'other'],
    category: 'short',
    localPreviewImage: getLocalPreviewImageFromId('men-low-taper-fade'),
    previewImage: 'https://images.unsplash.com/photo-1622286342621-4bd786c2447c?w=400&h=400&fit=crop',
    promptStyle: "a man with this haircut: Subtle taper with smooth transition—clean and effortless."
  },
  {
    id: 'men-classic-side-part',
    name: 'Curtains (Middle-Part)',
    genderTags: ['men', 'other'],
    category: 'medium',
    localPreviewImage: getLocalPreviewImageFromId('men-classic-side-part'),
    previewImage: 'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=400&h=400&fit=crop',
    promptStyle: "a man with this haircut: Mid-length with center part—’90s meets now."
  },
  {
    id: 'men-buzz-cut',
    name: 'Buzz with Texture',
    genderTags: ['men', 'other'],
    category: 'short',
    localPreviewImage: getLocalPreviewImageFromId('men-buzz-cut'),
    previewImage: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop',
    promptStyle: "a man with this haircut: Very short buzz with surface texture—rugged but clean."
  },
  {
    id: 'men-curly-top-fade',
    name: 'Curly Taper',
    genderTags: ['men', 'other'],
    category: 'medium',
    localPreviewImage: getLocalPreviewImageFromId('men-curly-top-fade'),
    previewImage: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=400&h=400&fit=crop',
    promptStyle: "a man with this haircut: Curls on top with tapered sides and back."
  },
  {
    id: 'men-pompadour',
    name: 'Modern Pompadour',
    genderTags: ['men', 'other'],
    category: 'medium',
    localPreviewImage: getLocalPreviewImageFromId('men-pompadour'),
    previewImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop',
    promptStyle: "a man with this haircut: Voluminous swept-back top with fade/undercut sides."
  },
  {
    id: 'men-undercut',
    name: 'Two-Block (K-style)',
    genderTags: ['men', 'other'],
    category: 'medium',
    localPreviewImage: getLocalPreviewImageFromId('men-undercut'),
    previewImage: 'https://images.unsplash.com/photo-1564564321837-a57b7070ac4f?w=400&h=400&fit=crop',
    promptStyle: "a man with this haircut: Long top 'block' with short lower block—Ostentatiously K-pop."
  },
  {
    id: 'men-quiff',
    name: 'Edgar',
    genderTags: ['men', 'other'],
    category: 'medium',
    localPreviewImage: getLocalPreviewImageFromId('men-quiff'),
    previewImage: 'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=400&h=400&fit=crop',
    promptStyle: "a man with this haircut: Blunt straight fringe with high fade—sharp and bold."
  },
  {
    id: 'men-wolf-cut',
    name: 'Wolf Cut',
    genderTags: ['men', 'other'],
    category: 'medium',
    localPreviewImage: getLocalPreviewImageFromId('men-wolf-cut'),
    previewImage: 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=400&h=400&fit=crop',
    promptStyle: "a man with this haircut: Layered, shaggy volume—modern 'wolf' meets mullet."
  },
  {
    id: 'men-burst-fade-mullet',
    name: 'Burst Fade Mullet',
    genderTags: ['men', 'other'],
    category: 'medium',
    localPreviewImage: getLocalPreviewImageFromId('men-burst-fade-mullet'),
    previewImage: 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=400&h=400&fit=crop',
    promptStyle: 'a man with this haircut: Fade around ears with longer back—modern mullet vibe.'
  },

  // ===== WOMEN'S HAIRCUTS =====
  {
    id: 'women-bixie-cut',
    name: 'Bixie Cut',
    genderTags: ['women', 'other'],
    category: 'short',
    localPreviewImage: '/assets/haircuts/women/bixie_cut_women.jpeg',
    previewImage: '/assets/haircuts/women/bixie_cut_women.jpeg',
    promptStyle: "a woman with this haircut: A hybrid between a bob and a pixie. A textured, layered haircut sitting at jaw-length, featuring choppy layers, a side-swept fringe, and a tousled, wavy finish. Versatile and modern."
  },
  {
    id: 'women-power-pixie',
    name: 'Power Pixie',
    genderTags: ['women', 'other'],
    category: 'short',
    localPreviewImage: '/assets/haircuts/women/power_pixie_women.jpeg',
    previewImage: '/assets/haircuts/women/power_pixie_women.jpeg',
    promptStyle: "a woman with this haircut: A short, textured 'Power Pixie' haircut. The style features longer, choppy layers on the crown and top that are styled with volume and piecey movement, swept slightly forward and to the side. The sides and nape are tapered and sculpted close to the head, creating a defined silhouette. The overall look is bold, modern, and confident."
  },
  {
    id: 'women-classic-pixie-cut',
    name: 'Classic Pixie Cut',
    genderTags: ['women', 'other'],
    category: 'short',
    localPreviewImage: '/assets/haircuts/women/classic_pixie_cut_women.jpeg',
    previewImage: '/assets/haircuts/women/classic_pixie_cut_women.jpeg',
    promptStyle: 'a woman with this haircut: A Classic Pixie Cut. A short, neat, and timeless hairstyle where the hair is cropped close to the head on the sides and back, with slightly longer, layered hair on top that is styled smoothly forward with a soft, wispy bang. The overall silhouette is sleek and defines the head shape.'
  },
  {
    id: 'women-ultra-short',
    name: 'Ultra-Short',
    genderTags: ['women', 'other'],
    category: 'short',
    localPreviewImage: '/assets/haircuts/women/ultra_short_women.jpeg',
    previewImage: '/assets/haircuts/women/ultra_short_women.jpeg',
    promptStyle: 'a woman with this haircut: An ultra-short, modern pixie cut featuring a precision fade or undercut along the sides and back. The hair on top is kept slightly longer but still very short, texturized, and styled forward for a sharp, edgy, and clean look.'
  },
  {
    id: 'women-long-bob-lob',
    name: 'Long Bob (Lob)',
    genderTags: ['women', 'other'],
    category: 'medium',
    localPreviewImage: '/assets/haircuts/women/long_bob_lob_women.png',
    previewImage: '/assets/haircuts/women/long_bob_lob_women.png',
    promptStyle: 'a woman with this haircut: A collarbone-length Long Bob (Lob) haircut. The hair is blonde with dimensional highlights and is styled with loose, textured, beachy waves and a center part. The ends are relatively blunt but softened by the wave texture.'
  },
  {
    id: 'women-textured-box-bob',
    name: 'Textured Box Bob',
    genderTags: ['women', 'other'],
    category: 'short',
    localPreviewImage: '/assets/haircuts/women/textured_box_bob_women.png',
    previewImage: '/assets/haircuts/women/textured_box_bob_women.png',
    promptStyle: 'a woman with this haircut: A Textured Box Bob. A chin-length bob with a blunt, square silhouette softened by loose, natural texture and gentle waves. The ends feature a subtle undercurve, turning slightly inward to frame the jawline and face.'
  },
  {
    id: 'women-blunt-mid-length',
    name: 'Blunt Mid-Length',
    genderTags: ['women', 'other'],
    category: 'medium',
    localPreviewImage: '/assets/haircuts/women/blunt_mid_length_women.png',
    previewImage: '/assets/haircuts/women/blunt_mid_length_women.png',
    promptStyle: 'a woman with this haircut: Mid-Length Hair with Curtain Fringe. Collarbone-length hair with loose, textured waves, featuring a soft curtain fringe that is shorter in the center and graduates to longer lengths at the temples, effectively framing the face.'
  },
  {
    id: 'women-butterfly-cut',
    name: 'Butterfly Cut',
    genderTags: ['women', 'other'],
    category: 'medium',
    localPreviewImage: '/assets/haircuts/women/butterfly_cut_women.png',
    previewImage: '/assets/haircuts/women/butterfly_cut_women.png',
    promptStyle: 'a woman with this haircut: A medium-length Butterfly Cut. This style features heavy, soft, face-framing layers that start around the cheekbones and feather outwards, blending into longer lengths past the collarbone. It is paired with wispy, see-through bangs that graze the eyebrows. The hair is styled with loose, bouncy waves to create maximum soft volume and movement.'
  },
  {
    id: 'women-birkin-bangs',
    name: 'Birkin Bangs',
    genderTags: ['women', 'other'],
    category: 'medium',
    localPreviewImage: '/assets/haircuts/women/birkin_bangs_women.jpeg',
    previewImage: '/assets/haircuts/women/birkin_bangs_women.jpeg',
    promptStyle: "a woman with this haircut: A medium-length, wavy brown haircut with a distinct 70s-inspired vibe. The defining feature is the long, wispy, and piece-y 'Birkin Bangs' that fall to the eyelashes and blend softly into face-framing layers. The rest of the hair has a natural, undone, and slightly shaggy texture."
  },
  {
    id: 'women-sixties-inspired-bangs',
    name: '60s-Inspired Bangs',
    genderTags: ['women', 'other'],
    category: 'long',
    localPreviewImage: '/assets/haircuts/women/sixties_inspired_bangs_women.png',
    previewImage: '/assets/haircuts/women/sixties_inspired_bangs_women.png',
    promptStyle: 'a woman with this haircut: Long Sleek Hair with Heavy Blunt Bangs. A dramatic and bold style featuring long, straight hair paired with a thick, full fringe cut in a precise, straight line just below the eyebrows.'
  },
  {
    id: 'women-sultry-brunette',
    name: 'Sultry Brunette',
    genderTags: ['women', 'other'],
    category: 'long',
    localPreviewImage: '/assets/haircuts/women/sultry_brunette_women.png',
    previewImage: '/assets/haircuts/women/sultry_brunette_women.png',
    promptStyle: 'a woman with this haircut: A long, luxurious brunette haircut with soft, cascading layers that begin below the chin and continue to the ends, adding movement and body. The look is defined by long curtain bangs that are parted in the middle and sweep outwards to frame the cheekbones and eyes. The texture is smooth and polished.'
  },
  {
    id: 'women-voluminous-bombshell-blowout',
    name: 'Voluminous Bombshell Blowout',
    genderTags: ['women', 'other'],
    category: 'long',
    localPreviewImage: '/assets/haircuts/women/voluminous_bombshell_blowout_women.png',
    previewImage: '/assets/haircuts/women/voluminous_bombshell_blowout_women.png',
    promptStyle: "a woman with this haircut: A glamorous, high-fashion hairstyle on long, multi-dimensional blonde hair. Features a deep side part with a dramatic swoop and long layers styled into massive, bouncy, voluminous waves for a polished 'supermodel' look."
  },
  {
    id: 'women-shag-mullet',
    name: 'Shag Mullet',
    genderTags: ['women', 'other'],
    category: 'medium',
    localPreviewImage: '/assets/haircuts/women/shag_mullet_women.png',
    previewImage: '/assets/haircuts/women/shag_mullet_women.png',
    promptStyle: 'a woman with this haircut: A highly textured, modern shag mullet. This cut features a distinct disconnection between short, choppy, voluminous layers on the crown and top, and significantly longer, thinned-out lengths at the back. It is defined by heavy, parted curtain bangs that blend into short, face-framing side layers styled with an outward flick. The overall finish is matte, tousled, and effortlessly undone.'
  },

  // ===== OTHER (ANDROGYNOUS/UNISEX) =====
  // Other uses the SAME presets (and same images/prompts) as Men + Women.
  // We accomplish this by including "other" in the genderTags for all men/women presets above.
];

const inferLockHairLength = (preset) => {
  const category = String(preset?.category || '').toLowerCase();
  if (category === 'short' || category === 'medium' || category === 'long') return category;
  return 'medium';
};

const inferLockFadeType = (preset) => {
  const haystack = `${preset?.id || ''} ${preset?.name || ''} ${preset?.promptStyle || ''}`.toLowerCase();
  if (haystack.includes('skin fade')) return 'skin';
  if (haystack.includes('burst fade')) return 'burst';
  if (haystack.includes('high fade')) return 'high';
  if (haystack.includes('mid fade')) return 'mid';
  if (haystack.includes('low fade') || haystack.includes('low taper')) return 'low';
  if (haystack.includes('taper')) return 'taper';
  return 'none';
};

const inferLockTexture = (preset) => {
  const haystack = `${preset?.id || ''} ${preset?.name || ''} ${preset?.promptStyle || ''}`.toLowerCase();
  if (haystack.includes('textured')) return 'textured';
  if (haystack.includes('curly') || haystack.includes('coily')) return 'curly';
  if (haystack.includes('wavy')) return 'wavy';
  if (haystack.includes('sleek') || haystack.includes('smooth')) return 'sleek';
  return 'natural';
};

const toGenerationLock = (preset) => ({
  presetId: preset.id,
  styleName: preset.name,
  category: preset.category || 'medium',
  hairLength: inferLockHairLength(preset),
  fadeType: inferLockFadeType(preset),
  texture: inferLockTexture(preset),
  styleDirective: preset.promptStyle || '',
  strictConstraints: [
    'Do not switch to another hairstyle family.',
    'Keep haircut silhouette and length category consistent with this preset.',
    'Only adapt micro-details to the visible hair texture and density.',
  ],
});

export const haircutPresets = rawHaircutPresets.map((preset) => ({
  ...preset,
  generationLock: preset.generationLock || toGenerationLock(preset),
}));

// Filter presets by gender
export const getPresetsByGender = (gender) => {
  const g = (gender || 'other').toLowerCase();

  // For "other", show the same men + women presets but interleave them
  // (one men, one women) for a mixed, balanced gallery.
  if (g === 'other') {
    const menPresets = haircutPresets.filter(preset => preset.genderTags.includes('men'));
    const womenPresets = haircutPresets.filter(preset => preset.genderTags.includes('women'));

    const mixed = [];
    const maxLen = Math.max(menPresets.length, womenPresets.length);
    for (let i = 0; i < maxLen; i += 1) {
      if (menPresets[i]) mixed.push(menPresets[i]);
      if (womenPresets[i]) mixed.push(womenPresets[i]);
    }
    return mixed;
  }

  return haircutPresets.filter((preset) => preset.genderTags.includes(g));
};

// Get preset by ID
export const getPresetById = (id) => {
  return haircutPresets.find(preset => preset.id === id);
};

// Hair color options with solid color cards (NO people images)
// Trendy color set as requested
export const hairColorOptions = [
  {
    id: 'no-change',
    name: 'No Change',
    displayName: 'Original',
    colorHex: null,
    promptText: null
  },
  {
    id: 'black',
    name: 'Black',
    displayName: 'Black',
    colorHex: '#1C1C1C',
    promptText: 'black hair'
  },
  {
    id: 'espresso-brown',
    name: 'Espresso Brown',
    displayName: 'Espresso',
    colorHex: '#3B2414',
    promptText: 'espresso brown hair'
  },
  {
    id: 'chocolate-brown',
    name: 'Chocolate Brown',
    displayName: 'Chocolate',
    colorHex: '#5C4033',
    promptText: 'chocolate brown hair'
  },
  {
    id: 'honey-blonde',
    name: 'Honey Blonde',
    displayName: 'Honey',
    colorHex: '#D4A574',
    promptText: 'honey blonde hair'
  },
  {
    id: 'platinum',
    name: 'Platinum',
    displayName: 'Platinum',
    colorHex: '#E5E4E2',
    promptText: 'platinum blonde hair'
  },
  {
    id: 'copper',
    name: 'Copper',
    displayName: 'Copper',
    colorHex: '#B87333',
    promptText: 'copper hair'
  },
  {
    id: 'ash-brown',
    name: 'Ash Brown',
    displayName: 'Ash',
    colorHex: '#826C5E',
    promptText: 'ash brown hair'
  },
  {
    id: 'soft-gray',
    name: 'Soft Gray',
    displayName: 'Soft Gray',
    colorHex: '#A8A8A8',
    promptText: 'soft gray hair'
  },
  {
    id: 'rose-gold',
    name: 'Rose Gold',
    displayName: 'Rose Gold',
    colorHex: '#E0BFB8',
    promptText: 'rose gold hair'
  },
  {
    id: 'ice-blonde',
    name: 'Ice Blonde',
    displayName: 'Ice',
    colorHex: '#F5F5DC',
    promptText: 'ice blonde hair'
  },
];

export const getHairColorById = (id) => {
  return hairColorOptions.find(color => color.id === id);
};

